// Lightweight error handling utilities for consistent user-facing messages.

export type ApiError = {
  message?: string;
  code?: string | number;
  details?: unknown;
};

/**
 * Error categories for better error handling decisions
 */
export type ErrorCategory =
  | 'network'      // Network connectivity issues
  | 'auth'         // Authentication/authorization errors
  | 'validation'   // Input validation errors
  | 'not_found'    // Resource not found
  | 'conflict'     // Conflict (e.g., duplicate entry)
  | 'server'       // Server-side errors
  | 'timeout'      // Request timeout
  | 'cancelled'    // Request was cancelled
  | 'unknown';     // Unknown error type

/**
 * Extended error information with category
 */
export interface ParsedError extends ApiError {
  category: ErrorCategory;
  isRetryable: boolean;
  httpStatus?: number;
}

/**
 * Check if an error is a network connectivity error
 */
export function isNetworkError(err: unknown): boolean {
  const e = err as any;
  return !!(e && (
    e.isAxiosError && !e.response ||
    e.name === 'FetchError' ||
    e.message?.includes('Network') ||
    e.message?.includes('Failed to fetch') ||
    e.message?.includes('ERR_NETWORK') ||
    e.code === 'ECONNREFUSED' ||
    e.code === 'ENOTFOUND'
  ));
}

/**
 * Check if an error is a timeout error
 */
export function isTimeoutError(err: unknown): boolean {
  const e = err as any;
  return !!(e && (
    e.code === 'ECONNABORTED' ||
    e.code === 'ETIMEDOUT' ||
    e.message?.includes('timeout') ||
    e.name === 'TimeoutError'
  ));
}

/**
 * Check if an error is an abort/cancellation error
 */
export function isAbortError(err: unknown): boolean {
  const e = err as any;
  return !!(e && (
    e.name === 'AbortError' ||
    e.name === 'CanceledError' ||
    e.code === 'ERR_CANCELED' ||
    e.message?.includes('aborted') ||
    e.message?.includes('cancelled') ||
    e.message?.includes('canceled')
  ));
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(err: unknown): boolean {
  const e = err as any;
  const status = e?.response?.status || e?.status;
  return status === 401 || status === 403;
}

/**
 * Determine the error category from an error object
 */
export function getErrorCategory(err: unknown): ErrorCategory {
  if (isAbortError(err)) return 'cancelled';
  if (isNetworkError(err)) return 'network';
  if (isTimeoutError(err)) return 'timeout';
  if (isAuthError(err)) return 'auth';

  const e = err as any;
  const status = e?.response?.status || e?.status;

  if (status === 400) return 'validation';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status >= 500) return 'server';

  return 'unknown';
}

/**
 * Determine if an error is worth retrying
 */
export function isRetryableError(err: unknown): boolean {
  const category = getErrorCategory(err);
  // Network, timeout, and server errors are typically retryable
  return ['network', 'timeout', 'server'].includes(category);
}

export function parseApiError(err: unknown): ApiError {
  const e = err as any;

  // Backend often rejects with Result<T> directly (see requestHandling.ts), not an AxiosError.
  // Shape: { status, payload, errors: [{ code, description, path, details? }] }
  try {
    const resultErrors = e?.errors;
    if (Array.isArray(resultErrors) && resultErrors.length > 0) {
      const first = resultErrors[0];
      const msg = (first?.description || first?.Description || first?.message || first?.Message);
      return {
        message: typeof msg === 'string' && msg.trim().length > 0 ? msg : 'An unexpected error occurred.',
        code: e?.status || first?.code || first?.Code,
        details: e,
      };
    }
  } catch {
    // ignore
  }

  // Axios-like response shape
  const axiosMsg = e?.response?.data?.message || e?.response?.data?.error || e?.response?.data;
  const axiosCode = e?.response?.status || e?.code;

  // Fetch-like
  const fetchMsg = e?.message;

  const message = (axiosMsg && typeof axiosMsg === 'string')
    ? axiosMsg
    : (typeof fetchMsg === 'string' ? fetchMsg : 'An unexpected error occurred.');

  return {
    message,
    code: axiosCode,
    details: e?.response?.data || e,
  };
}

/**
 * Parse an error with extended information including category and retryability
 */
export function parseApiErrorExtended(err: unknown): ParsedError {
  const base = parseApiError(err);
  const category = getErrorCategory(err);
  const e = err as any;

  return {
    ...base,
    category,
    isRetryable: isRetryableError(err),
    httpStatus: e?.response?.status || e?.status,
  };
}

/**
 * Get a user-friendly error message based on error category
 */
export function getCategoryMessage(category: ErrorCategory): string {
  const messages: Record<ErrorCategory, string> = {
    network: 'Unable to connect. Please check your internet connection.',
    auth: 'Your session has expired. Please sign in again.',
    validation: 'Please check your input and try again.',
    not_found: 'The requested resource was not found.',
    conflict: 'Your changes conflict with another update. Please refresh and try again.',
    server: 'Server error. Please try again in a moment.',
    timeout: 'The request took too long. Please try again.',
    cancelled: 'The request was cancelled.',
    unknown: 'Something went wrong. Please try again.',
  };
  return messages[category];
}

export function formatErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  // Don't show error messages for cancelled requests
  if (isAbortError(err)) {
    return '';
  }

  try {
    const parsed = parseApiError(err);
    const base = parsed.message?.trim();

    // If no message, use category-based message
    if (!base || base.length === 0) {
      const category = getErrorCategory(err);
      return getCategoryMessage(category);
    }

    // Compact overly long messages
    return base.length > 300 ? base.slice(0, 297) + '...' : base;
  } catch {
    return fallback;
  }
}

/**
 * Extract the first API error message from a rejected promise.
 * The Axios error interceptor rejects with response.data (Result<T> shape),
 * so the caught value has { errors: [{ description, code, path }], status, payload }.
 */
export function extractApiError(err: unknown, fallback: string): string {
  try {
    const e = err as any;
    if (Array.isArray(e?.errors) && e.errors.length > 0) {
      const first = e.errors[0];
      const msg: unknown = first?.description || first?.message;
      if (typeof msg === 'string' && msg.trim().length > 0) return msg;
    }
  } catch { /* ignore */ }
  return fallback;
}

// Simple notifier abstraction. Integrate with your toast system if available.
export function notifyError(err: unknown, notify?: (msg: string) => void) {
  // Don't notify for cancelled requests
  if (isAbortError(err)) {
    return;
  }

  const message = formatErrorMessage(err);
  if (!message) return;

  if (typeof notify === 'function') {
    notify(message);
    return;
  }
  // Fallback to console if no notifier is provided
  console.error(message, err);
}

/**
 * Create a retry wrapper for async functions with exponential backoff
 *
 * @example
 * ```ts
 * const fetchWithRetry = withRetry(fetchData, { maxRetries: 3 });
 * const result = await fetchWithRetry(userId);
 * ```
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in ms before first retry (default: 1000) */
  initialDelay?: number;
  /** Maximum delay in ms between retries (default: 10000) */
  maxDelay?: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number;
  /** Custom function to determine if error should be retried */
  shouldRetry?: (err: unknown, attempt: number) => boolean;
  /** Callback on each retry attempt */
  onRetry?: (err: unknown, attempt: number, delay: number) => void;
}

export function withRetry<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: RetryOptions = {}
): T {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    shouldRetry = isRetryableError,
    onRetry,
  } = options;

  return (async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    let lastError: unknown;
    let delay = initialDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args);
      } catch (err) {
        lastError = err;

        // Don't retry if we've exhausted attempts or error isn't retryable
        if (attempt >= maxRetries || !shouldRetry(err, attempt)) {
          throw err;
        }

        // Calculate delay with jitter to prevent thundering herd
        const jitter = Math.random() * 0.3 + 0.85; // 0.85-1.15x
        const actualDelay = Math.min(delay * jitter, maxDelay);

        onRetry?.(err, attempt + 1, actualDelay);

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, actualDelay));

        // Increase delay for next attempt
        delay = Math.min(delay * backoffMultiplier, maxDelay);
      }
    }

    throw lastError;
  }) as T;
}

/**
 * Create an abort-aware wrapper for async functions
 *
 * @example
 * ```ts
 * const controller = new AbortController();
 * const abortableFetch = withAbort(fetchData, controller.signal);
 *
 * // Later: controller.abort();
 * ```
 */
export function withAbort<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  signal: AbortSignal
): T {
  return (async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    // Check if already aborted
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Create a promise that rejects when aborted
    const abortPromise = new Promise<never>((_, reject) => {
      const abortHandler = () => {
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal.addEventListener('abort', abortHandler, { once: true });
    });

    // Race between the actual function and abort
    return Promise.race([fn(...args), abortPromise]);
  }) as T;
}
